<script setup lang="ts">
import { ROUTE_BLOG_CATEGORY_PAGINATION } from "~/constants/routes";

definePageMeta({
  layout: "blog-page-category",
  scrollToTop: true,
});

const route = useRoute();
const router = useRouter();
const { replaceParams } = useRouteParams();
const {
  public: { postsPerPage },
} = useRuntimeConfig();

const pageNumber = computed(() => (route.params.number as string) || "1");

const categorySlugParam = computed(() => route.params.slug as string);

const slug = computed(() => route.params.slug);

const dataKey = computed(() => `blog-cat-${slug.value}-p${pageNumber.value}`);

const { data } = await useAsyncData(dataKey.value, async () => {
  const [categoriesResponse, categoryResponse, categoryPostsResponse] =
    await Promise.all([
      $fetch("/api/content/categories"),
      $fetch(`/api/content/categories/${categorySlugParam.value}`),
      $fetch(
        `/api/content/categories/${categorySlugParam.value}/pages/${pageNumber.value}?perPage=${postsPerPage}`
      ),
    ]);

  return {
    categories: Object.values(categoriesResponse) ?? [],
    categoryPostsResponse,
    category: categoryResponse,
  };
});

const categories = computed(() => data?.value?.categories ?? []);

const category = computed(() => data?.value?.category);

const postsResponse = computed(() => data.value?.categoryPostsResponse ?? null);

setSEO({
  seo: category?.value?.seo,
  title: `Page ${pageNumber.value} | ${category?.value?.seo?.title}`,
});

function goToPreviousPage() {
  router.push({
    path: replaceParams(ROUTE_BLOG_CATEGORY_PAGINATION, {
      slug: slug.value as string,
      number: parseInt(pageNumber.value, 10) - 1,
    }),
  });
}

function goToNextPage() {
  router.push({
    path: replaceParams(ROUTE_BLOG_CATEGORY_PAGINATION, {
      slug: slug.value as string,
      number: parseInt(pageNumber.value, 10) + 1,
    }),
  });
}
</script>

<template>
  <section class="tw-pb-[60px] sm:tw-pb-[85px] tw-mt-10">
    <header
      class="tw-max-w-[1180px] tw-px-[17px] tw-mx-auto tw-mb-4"
    >
      <h2
        class="tw-font-agenda tw-font-bold tw-text-[42px] md:tw-text-[76px] tw-leading-[0.86] tw-uppercase tw-text-center"
      >
        {{ category?.name }}
      </h2>
    </header>
    <BlogPageCategoryNavbar :categories="categories">
      <template #search><BlogSearch /></template>
    </BlogPageCategoryNavbar>
    <BlogPostList
      v-if="
        postsResponse &&
          postsResponse.content &&
          postsResponse.content.length > 0
      "
      :posts="postsResponse?.content"
    />
    <div v-else class="tw-container !tw-max-w-[1174px]">
      <div
        class="tw-px-4 md:tw-px-[33px] tw-w-full sm:tw-w-1/2 md:tw-w-1/3 tw-mb-[35px] sm:tw-mb-[59px] post-card"
      >
        <strong
          class="tw-block tw-text-[12px] sm:tw-text-[18px] tw-nl-text-green-200 tw-font-medium tw-mb-0.5"
        >
          Nothing found.
        </strong>

        <p class="tw-text-base sm:tw-text-[22px] sm:tw-max-w-[300px]">
          We could not find any posts that match your search query.
        </p>
      </div>
    </div>
    <Pagination
      v-show="
        postsResponse &&
          postsResponse.content &&
          postsResponse.content.length > 0
      "
      :is-prev-disabled="Number(pageNumber) <= 1"
      :is-next-disabled="!Boolean(postsResponse?.nextPage)"
      :go-to-previous-page="goToPreviousPage"
      :go-to-next-page="goToNextPage"
    />
  </section>
</template>
